import {
  NEW_ROUND_FUND_INVESTMENT_LABEL,
  NEW_ROUND_FUND_INVESTMENT_TOOLTIP,
  NEW_ROUND_FUND_LABEL,
  NEW_ROUND_FUND_OWNERSHIP_HEADER,
  NEW_ROUND_FUND_OWNERSHIP_PCT_LABEL,
  NEW_ROUND_NUMBER_OF_SHARES_LABEL,
  NEW_ROUND_NUMBER_OF_SHARES_TOOLTIP,
} from 'common/constants/cap-table';
import { capTableTwoDecimalPercentFormat, costBasisCurrencyFormat } from 'common/formats/formats';
import { GridSelect } from 'components/FeaturedSpreadsheet/components';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import SelectValueViewer from 'components/SelectValueViewer';
import { NEW_ROUND_MODELER_TEST_IDS } from '../NewRoundModeler.testIds';
import { isFundInvestmentExceedingAmountInvested } from '../NewRoundModeler.utils';
import { labelCell, numberCell, percentCell, sectionHeaderRow, VALUE_STYLE } from './sheetCellHelpers';
import { NrmParserProps, NrmTableData } from './types';

const fundOwnershipParser = ({ tableData }: NrmParserProps) => {
  const fundOptions = tableData.fundList.map(fund => ({ value: fund.id, label: fund.name }));
  const hasFundInvestment = (Number(tableData.fundInvestment) || 0) > 0;
  const fundInvestmentExceedsAmount = isFundInvestmentExceedingAmountInvested(
    tableData.fundInvestment,
    tableData.amountInvested,
  );

  return {
    ...sectionHeaderRow(NEW_ROUND_FUND_OWNERSHIP_HEADER),
    A2: labelCell(NEW_ROUND_FUND_LABEL),
    A3: labelCell(NEW_ROUND_FUND_INVESTMENT_LABEL, NEW_ROUND_FUND_INVESTMENT_TOOLTIP),
    A4: labelCell(NEW_ROUND_FUND_OWNERSHIP_PCT_LABEL),
    A5: labelCell(NEW_ROUND_NUMBER_OF_SHARES_LABEL, NEW_ROUND_NUMBER_OF_SHARES_TOOLTIP),
    B2: {
      key: 'B2',
      alias: 'fundId',
      value: tableData.fundId,
      gridType: 'string' as const,
      dropdown: true,
      readOnly: false,
      dataEditor: (props: any) => <GridSelect menuPosition="absolute" options={fundOptions} {...props} />,
      valueViewer: (props: any) => <SelectValueViewer options={fundOptions} {...props} />,
      style: VALUE_STYLE,
    },
    B3: {
      key: 'B3',
      alias: 'fundInvestment',
      value: Number(tableData.fundInvestment) || 0,
      gridType: 'number' as const,
      format: costBasisCurrencyFormat,
      readOnly: false,
      isRequired: true,
      greaterThan: 0,
      style: VALUE_STYLE,
      testId: NEW_ROUND_MODELER_TEST_IDS.fundInvestment,
    },
    B4: {
      ...(hasFundInvestment && !fundInvestmentExceedsAmount
        ? percentCell(tableData.computed?.fundOwnershipPct, capTableTwoDecimalPercentFormat)
        : percentCell(null, capTableTwoDecimalPercentFormat)),
      testId: NEW_ROUND_MODELER_TEST_IDS.fundOwnershipPct,
    },
    B5: hasFundInvestment
      ? numberCell(tableData.computed ? Math.round(tableData.computed.numberOfShares) : null)
      : numberCell(null),
  };
};

const createFundOwnershipSheetConfig = (tableData: NrmTableData) =>
  new SpreadsheetConfig<object, object, object, object>({
    name: 'nrmFundOwnership',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parser: fundOwnershipParser as any,
    reverseParser: undefined,
    afterCellChanged: (changes: any) => changes,
    rowConfig: [{}, {}, {}, {}, {}],
    columns: [{}, {}],
    tableData,
    showTitlesColumn: false,
    hasColTitle: false,
    showToolbar: false,
    displayRowIndicator: false,
    displayColumnIndicator: false,
    currencyFormatter: false,
    unitsFormatter: false,
    ignoreCellExpressionErrors: false,
    // SpreadsheetConfigConstructorParams extends ScalarSpreadsheetConfigDataInterface which
    // requires internally-computed fields (evaluate, applyConditions, reverseParse, etc.)
    // that the constructor populates itself — not caller inputs.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

export default createFundOwnershipSheetConfig;
